import { useLocation } from 'react-router';
import Stack from '@mui/material/Stack';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { IPortfolioItemReportFragment } from 'graphql/types';
import { TableCell } from 'common/components/Table';
import { UserAvatar } from 'user/components/UserAvatar';
import { Chip } from 'common/components/Chip';
import { LinearProgress } from 'common/components/LinearProgress';
import { Checkbox } from 'common/components/Checkbox';
import {
    formatDate,
    timeDeltaToDuration,
    durationToHuman,
} from 'common/utils/formatDate';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';

interface IProps {
    reportItem: IPortfolioItemReportFragment;
    isSelected: boolean;
    hideCheckbox?: boolean;
    onSelect?: () => void;
}

export const CourseReportTableRow = ({
    reportItem,
    isSelected,
    hideCheckbox,
    onSelect,
}: IProps) => {
    const location = useLocation();
    const { openGlobalDrawer } = useGlobalDrawer();

    const {
        id,
        title,
        user,
        userName,
        startDate,
        completionDate,
        timeSpent,
        progress,
        score,
        scoreThreshold,
    } = reportItem;
    // When no user is set, fall back to userName.
    const name = user?.name ?? userName ?? undefined;

    if (!user && !userName) return null;

    const startDateString = startDate && formatDate(startDate, 'dd MMMM yyyy');
    const completionDateString =
        completionDate && formatDate(completionDate, 'dd MMMM yyyy');

    const timeSpentDuration = timeDeltaToDuration(
        timeSpent || undefined
    ).shiftTo('days', 'hours', 'minutes', 'seconds');

    const showScore = progress === 1 && score;

    const scoreChip = showScore ? (
        <Chip
            color={score >= (scoreThreshold || 0) ? 'success' : 'error'}
            label={`${Math.round(score * 100)}%`}
        />
    ) : null;

    const onClickRow = () => {
        openGlobalDrawer(
            {
                type: IGlobalDrawerType.PortfolioItem,
                itemId: id,
            },
            location
        );
    };

    return (
        <TableRow
            hover
            key={id}
            sx={{ cursor: 'pointer' }}
            onClick={onClickRow}
        >
            {!hideCheckbox && (
                <TableCell>
                    <Checkbox
                        checked={isSelected}
                        onClick={(e) => {
                            onSelect?.();

                            e.stopPropagation();
                        }}
                    />
                </TableCell>
            )}
            <TableCell>
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{ alignItems: 'center' }}
                >
                    <UserAvatar
                        bgColor={user ? undefined : 'primary'}
                        size={35}
                        // When no user is set, render a name-only avatar.
                        user={
                            user ?? (userName ? { name: userName } : undefined)
                        }
                    />
                    <Typography variant="body2">{name}</Typography>
                </Stack>
            </TableCell>
            <TableCell>{title}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {startDateString}
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {completionDate ? (
                    <Typography variant="body2">
                        {completionDateString}
                    </Typography>
                ) : (
                    <LinearProgress
                        withProgressText
                        value={(progress || 0) * 100}
                    />
                )}
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {timeSpentDuration &&
                    durationToHuman(timeSpentDuration, {
                        unitDisplay: 'narrow',
                    })}
            </TableCell>
            <TableCell>{scoreChip}</TableCell>
        </TableRow>
    );
};
